import { Box, Button, Dialog, DialogContent, DialogTitle, TextField, Typography } from '@mui/material';
import * as Sentry from '@sentry/react';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { authProposalForm } from 'api';
import ErrorFallback, { ChunkReloadKeyCleanup } from 'components/Error/ErrorFallback';
import { SignupRouteTracker } from 'components/SignupRouteTracker/SignupRouteTracker';
import { SpinnerOverlay } from 'components/Spinner';
import { createLocation } from 'history';
import { queryClient } from 'lib/queryClient';
import { PostHogProvider } from 'posthog-js/react';
import { AppConfigProvider } from 'providers/app-config';
import queryString from 'query-string';
import { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import ReactGA from 'react-ga';
import {
    createBrowserRouter,
    Route,
    RouterProvider,
    Routes,
    ScrollRestoration,
    useLocation,
    useNavigate,
} from 'react-router-dom';
import { ProposalRedirect } from 'routes/ProposalRedirect';
import { AppWithUser } from 'routes/UserRoutes';
import { V1ToV2RedirectWrapper } from 'routes/V1ToV2RedirectWrapper';
import { V2RsvpRedirect } from 'routes/V2RsvpRedirect';
import { CurrencyStoreInitializer } from 'stores/currency/CurrencyStoreInitializer';
import { LoadMeasurementUnits } from 'stores/measurement-units';
import { UserStoreInitializer } from 'stores/user/UserStoreInitializer';
import { posthog } from 'utils/posthogClient';
import { setCookie } from 'utils/tokens';
import { emailIsValid, takeFirstIfArray } from './util';

// Lazy load page components for code splitting
const EventRegistrationV2 = lazy(() => import('pages/EventRegistrationV2'));

const GOOGLE_ANALYTICS_ID =
    import.meta.env.VITE_APP_ENV === 'prod'
        ? import.meta.env.VITE_APP_GOOGLE_ANALYTICS_ID_PRODUCTION
        : import.meta.env.VITE_APP_GOOGLE_ANALYTICS_ID;

export type TResult = { code: number; message: string };
const isNotNetworkError = (e: unknown): e is TResult => {
    return !!e && typeof (e as TResult).code === 'number';
};

const Auth = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const queryParams = takeFirstIfArray(
        queryString.parse(location.search) as { [param: string]: string | string[] | null | undefined }
    );
    const [showEmailModal, setShowEmailModal] = useState(false);
    const [email, setEmail] = useState('');
    const [emailError, setEmailError] = useState('');
    const [authParams, setAuthParams] = useState<{
        venueId: string;
        proposalId: string;
        token: string;
        intendedLocation: ReturnType<typeof createLocation>;
    } | null>(null);
    const hasInitialized = useRef(false);

    useEffect(() => {
        // Prevent re-running if we've already initialized
        if (hasInitialized.current) {
            return;
        }

        const intendedPage = decodeURI(queryParams.redirect ?? '/');
        const intendedLocation = createLocation(intendedPage);
        const redirectParam = encodeURIComponent(intendedPage);
        const newLocationSearch = takeFirstIfArray(
            queryString.parse(intendedLocation.search) as { [param: string]: string | string[] | null | undefined }
        );

        const proposalMatch = intendedLocation.pathname.match(/\/proposals\/(\d+)/);
        const venueMatch = intendedLocation.pathname.match(/\/venues\/(\d+)/);

        if (!proposalMatch) {
            navigate(`/sign-in?redirect=${redirectParam}`, { replace: true });
            return;
        }

        const proposalId = proposalMatch[1];
        const venueId = venueMatch?.[1] || newLocationSearch.venueId;
        const token = queryParams.token ?? '';

        if (!proposalId || !token) {
            navigate('/sign-in', {
                state: {
                    error: {
                        code: 400,
                        message: 'Missing required authentication parameters.',
                    },
                },
            });
            return;
        }

        if (!venueId) {
            navigate('/sign-in', {
                state: {
                    error: {
                        code: 400,
                        message: 'Missing venue information for proposal authentication.',
                    },
                },
            });
            return;
        }

        // Mark as initialized and store auth params and show email modal
        hasInitialized.current = true;
        // Defer state updates to avoid cascading renders
        queueMicrotask(() => {
            setAuthParams({
                venueId: String(venueId),
                proposalId,
                token,
                intendedLocation,
            });
            setShowEmailModal(true);
        });
    }, [location.search, navigate]);

    const handleEmailSubmit = async () => {
        if (!email.trim()) {
            setEmailError('Email is required');
            return;
        }

        if (!emailIsValid(email.trim())) {
            setEmailError('Please enter a valid email address');
            return;
        }

        if (!authParams) {
            return;
        }

        setEmailError('');

        try {
            const response = await authProposalForm(
                authParams.venueId,
                authParams.proposalId,
                authParams.token,
                email.trim()
            );
            if (response.success && response.user) {
                setCookie(response.user.authToken);
                setShowEmailModal(false);
                navigate(authParams.intendedLocation.pathname + authParams.intendedLocation.search);
            } else {
                setEmailError('Authentication failed. Please try again.');
            }
        } catch (e) {
            let errorMessage = 'An error occurred. Please try again.';
            if (isNotNetworkError(e)) {
                errorMessage = e.message || errorMessage;
            } else if (e instanceof Error) {
                errorMessage = e.message || errorMessage;
            }

            setEmailError(errorMessage);
        }
    };

    const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setEmail(e.target.value);
        if (emailError) {
            setEmailError('');
        }
    };

    const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            handleEmailSubmit();
        }
    };

    if (showEmailModal) {
        return (
            <Dialog open={showEmailModal} onClose={() => {}} maxWidth="sm" fullWidth>
                <DialogTitle>
                    <Typography variant="h6" component="div">
                        Enter Your Email
                    </Typography>
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                            Please enter your email address to continue with proposal authentication.
                        </Typography>
                        <TextField
                            fullWidth
                            label="Email"
                            type="email"
                            value={email}
                            onChange={handleEmailChange}
                            onKeyPress={handleKeyPress}
                            error={!!emailError}
                            helperText={emailError}
                            autoFocus
                            required
                        />
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 1 }}>
                            <Button onClick={handleEmailSubmit} variant="contained" disabled={!email.trim()}>
                                Continue
                            </Button>
                        </Box>
                    </Box>
                </DialogContent>
            </Dialog>
        );
    }

    return <SpinnerOverlay />;
};

const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

const App = () => {
    const location = useLocation();
    const navigate = useNavigate();

    useEffect(() => {
        if (GOOGLE_ANALYTICS_ID) {
            ReactGA.pageview(location.pathname + location.search);
        }
    }, [location]);

    return (
        <PostHogProvider client={posthog}>
            <SignupRouteTracker />
            <ErrorBoundary FallbackComponent={ErrorFallback} onReset={() => navigate(0)}>
                <QueryClientProvider client={queryClient}>
                    <AppConfigProvider>
                        <LoadMeasurementUnits />
                        <V1ToV2RedirectWrapper>
                            <Suspense fallback={<SpinnerOverlay />}>
                                <ChunkReloadKeyCleanup />
                                <SentryRoutes>
                                    <Route path="/auth" element={<Auth />} />
                                    <Route path="/events/:eventId/register/:slugId" element={<EventRegistrationV2 />} />
                                    <Route path="/v2/events/:eventId/rsvp" element={<V2RsvpRedirect />} />
                                    <Route path="/proposals/:proposalId" element={<ProposalRedirect />} />
                                    <Route
                                        path="*"
                                        element={
                                            <>
                                                <UserStoreInitializer />
                                                <CurrencyStoreInitializer />
                                                <AppWithUser />
                                            </>
                                        }
                                    />
                                </SentryRoutes>
                            </Suspense>
                        </V1ToV2RedirectWrapper>
                        <ScrollRestoration getKey={location => location.pathname} />
                    </AppConfigProvider>
                    {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
                </QueryClientProvider>
            </ErrorBoundary>
        </PostHogProvider>
    );
};

const router = createBrowserRouter([{ path: '*', element: <App /> }]);

const AppRouter = () => {
    return <RouterProvider router={router} />;
};

export default AppRouter;
